import { formatInTimeZone } from 'date-fns-tz';
import type { IntelSummary } from 'design-system';

import { http } from '@/services/http';

import type { MarketEventsFeed, SignalEvent, SignalsResponse } from '../types/marketEvents.types';

// signal_date is a date-only string (e.g. "2026-08-07") with no zone marker of
// its own, so appending `Z` forces UTC instead of leaving it to Date.parse's
// lenient (locale/timezone-dependent) fallback for anything non-ISO. This is
// the single source of truth for what counts as "parseable" below — both the
// epoch used for comparisons and the formatted string are derived from this
// one parse, so nothing downstream can disagree with it.
const toEpoch = (signalDate: string): number => Date.parse(`${signalDate}Z`);

// Falls back to '' for a malformed signal_date rather than throwing, so one
// bad record doesn't take down the whole mapping. Formats from the epoch
// (rather than re-parsing the original string) so the same parse governs both
// the parseability guard and the formatted output.
const formatTimestamp = (signalDate: string): string => {
  const epoch = toEpoch(signalDate);
  return Number.isNaN(epoch) ? '' : formatInTimeZone(epoch, 'UTC', 'd MMM yyyy');
};

const toIntelSummary = (event: SignalEvent): IntelSummary => ({
  id: event.id,
  title: event.title,
  importance: event.importance,
  region: event.series_id,
  timestamp: formatTimestamp(event.signal_date),
  blurb: event.description
});

/** The newest parseable `signal_date`, or null when there is none. */
const latestSignalDate = (events: SignalEvent[]): string | null => {
  let latest: string | null = null;
  let latestEpoch = Number.NEGATIVE_INFINITY;

  for (const event of events) {
    const epoch = toEpoch(event.signal_date);
    if (Number.isNaN(epoch) || epoch <= latestEpoch) continue;
    latestEpoch = epoch;
    latest = event.signal_date;
  }

  return latest;
};

// `?recent=true` already scopes results server-side (e.g. to accepted,
// current signals), so nothing here re-filters by status.
export const fetchMarketEvents = async (): Promise<MarketEventsFeed> => {
  const { data } = await http.internal.get<SignalsResponse>('/signals?recent=true');
  const items = Array.isArray(data?.items) ? data.items : [];
  const latest = latestSignalDate(items);

  return {
    signals: items.map(toIntelSummary),
    publishedAt: latest === null ? null : formatTimestamp(latest)
  };
};
